import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { chracterPosition } from '../../../utils/index';
import { colorCodesForAvatar } from '../../../constants/index';
import { ReactComponent as ConversationIcon } from '../../../assets/images/notifications/notification-conversation.svg';
import { ReactComponent as TicketIcon } from '../../../assets/images/notifications/notification-ticket.svg';
import { ReactComponent as KnowledgeIcon } from '../../../assets/images/notifications/notification-knowledge.svg';
import { ReactComponent as DocumentIcon } from '../../../assets/images/notifications/notification-document.svg';

const ICON_COMPONENTS = {
  conversation: ConversationIcon,
  ticket: TicketIcon,
  knowledge: KnowledgeIcon,
  document: DocumentIcon,
};

function initialsFromDisplayName(name) {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[1][0] || '';
    return `${a}${b}`.toUpperCase().slice(0, 2);
  }
  return trimmed.charAt(0).toUpperCase();
}

function avatarBackgroundColorForName(name) {
  const raw = name && String(name).trim();
  const letter = raw ? raw.charAt(0).toUpperCase() : 'A';
  const p = chracterPosition(letter);
  const idx = p > 0 ? p - 1 : 0;
  return colorCodesForAvatar[idx % colorCodesForAvatar.length];
}

function stripInlineTitleMarkup(s) {
  let t = String(s ?? '');
  t = t.replace(/<\/?\s*(strong|b)\s*>/gi, '');
  t = t.replace(/\*\*([^*]+)\*\*/g, '$1');
  t = t.replace(/\*([^*]+)\*/g, '$1');
  return t.replace(/\s+/g, ' ').trim();
}

function splitTitleForEmphasis(title) {
  const trimmed = stripInlineTitleMarkup(title);
  if (!trimmed) return { before: '', bold: '', after: '' };

  const kw = trimmed.match(/\b(chat|ticket|message)\b/i);
  if (kw && kw.index != null) {
    const i = kw.index;
    const len = kw[0].length;
    return {
      before: trimmed.slice(0, i),
      bold: trimmed.slice(i, i + len),
      after: trimmed.slice(i + len),
    };
  }

  const m = trimmed.match(/^(\S+)([\s\S]*)$/);
  if (!m) return { before: '', bold: trimmed, after: '' };
  return { before: '', bold: m[1], after: m[2] };
}

const titleLineSx = {
  color: '#061522',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: '18px',
};

const titleRegularSpanSx = {
  ...titleLineSx,
  fontWeight: 400,
  display: 'inline',
};

const titleEmphasisSpanSx = {
  ...titleLineSx,
  fontWeight: 500,
  display: 'inline',
};

const NotificationItem = ({ notification, onClick, avatarSize = 24 }) => {
  const {
    name,
    title,
    description,
    body,
    avatar,
    iconType,
    isRead,
  } = notification || {};

  const displayTitle = title ?? '';
  const displayDescription =
    description ?? body ?? '';
  const { before: titleBefore, bold: titleBold, after: titleAfter } =
    splitTitleForEmphasis(displayTitle);

  const IconComponent = iconType ? ICON_COMPONENTS[iconType] : null;
  const unread = isRead === false;
  const avatarInitials = name ? initialsFromDisplayName(name) : '';
  const avatarBgColor = name
    ? avatarBackgroundColorForName(name)
    : colorCodesForAvatar[0];
  const leadSize = avatarSize || 24;
  // Keep glyph at the original 14px design size; only the circle may grow
  const iconGlyph = 14;

  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px 0',
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor:  unread ? '#F9FAFA' : 'transparent',

        '&:hover': {
          backgroundColor:  '#F9FAFA',
          margin: '0 -16px',
          padding: '12px 16px',
          borderColor: unread ? '#148DFD' : 'transparent',
        },
      }}
    >
      {IconComponent ? (
        <Box
          sx={{
            width: leadSize,
            height: leadSize,
            minWidth: leadSize,
            minHeight: leadSize,
            borderRadius: '50%',
            backgroundColor: '#F9FAFA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            lineHeight: 0,
            boxSizing: 'border-box',
            '& svg': {
              width: `${iconGlyph}px`,
              height: `${iconGlyph}px`,
              display: 'block',
              flexShrink: 0,
            },
          }}
        >
          <IconComponent aria-hidden />
        </Box>
      ) : (
        <Avatar
          src={avatar}
          sx={{
            width: leadSize,
            height: leadSize,
            bgcolor: avatarBgColor,
            fontSize:
              leadSize >= 36
                ? avatarInitials.length > 1
                  ? '13px'
                  : '15px'
                : avatarInitials.length > 1
                  ? '9px'
                  : '11px',
            fontWeight: 600,
            fontFamily: 'Inter',
            flexShrink: 0,
            color: '#FFF',
          }}
        >
          {avatarInitials}
        </Avatar>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          variant="body2"
          sx={{
            ...titleLineSx,
            fontWeight: 400,
            wordBreak: 'break-word',
          }}
        >
          {titleBefore ? (
            <Box component="span" sx={titleRegularSpanSx}>
              {titleBefore}
            </Box>
          ) : null}
          {titleBold ? (
            <Box component="span" sx={titleEmphasisSpanSx}>
              {titleBold}
            </Box>
          ) : null}
          {titleAfter ? (
            <Box component="span" sx={titleRegularSpanSx}>
              {titleAfter}
            </Box>
          ) : null}
        </Typography>

        <Typography
          sx={{
            color: '#405261',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '18px',
            marginTop: '4px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {displayDescription}
        </Typography>
      </Box>
    </Box>
  );
};

export default NotificationItem;
